import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Settings, Plus, Trash2, Users, FileText,
  Building2, ChevronRight, Save, Loader2, Bell, HardDrive, ClipboardCheck, User,
  ChevronsUpDown, Check, Info, Plug, Eye, EyeOff, Copy, Zap, Mail, Upload, Image, X, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { usePermissions } from '../../hooks/usePermissions';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Switch } from '../../components/ui/switch';

const EntregaConfiguracion = () => {
  const { canEdit, canCreate, canDelete, isAdmin } = usePermissions();
  const readOnly = !canEdit('/entregas/configuracion') && !isAdmin();
  const [activeTab, setActiveTab] = useState('arquitectos');

  // Arquitectos
  const [asignaciones, setAsignaciones] = useState([]);
  const [masivaMap, setMasivaMap] = useState({});
  const [savingMasiva, setSavingMasiva] = useState(null);
  const [proyectos, setProyectos] = useState([]);
  const [proyectosConSubs, setProyectosConSubs] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingArq, setLoadingArq] = useState(false);
  const [savingArqCell, setSavingArqCell] = useState(null); // track which cell is saving
  const loadedTabs = React.useRef({});

  // Documentos
  const [documentos, setDocumentos] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [newDocNombre, setNewDocNombre] = useState('');
  const [newDocObligatorio, setNewDocObligatorio] = useState(true);

  // Notificaciones config
  const [configNotif, setConfigNotif] = useState([]);
  const [loadingNotif, setLoadingNotif] = useState(true);

  // Almacenamiento config
  const [configAlmac, setConfigAlmac] = useState({});
  const [loadingAlmac, setLoadingAlmac] = useState(true);
  const [savingAlmac, setSavingAlmac] = useState(false);
  const [testingAlmac, setTestingAlmac] = useState(false);

  // Checklist Pre-Entrega
  const [checklistItems, setChecklistItems] = useState([]);
  const [loadingChecklist, setLoadingChecklist] = useState(true);
  const [showAddChecklist, setShowAddChecklist] = useState(false);
  const [newCheckNombre, setNewCheckNombre] = useState('');
  const [newCheckResponsable, setNewCheckResponsable] = useState('');
  const [allUsuarios, setAllUsuarios] = useState([]);
  const [openComboResp, setOpenComboResp] = useState(false);

  // Modo asignación
  const [modoAsignacion, setModoAsignacion] = useState('proyecto_etapa');
  const [areasRR, setAreasRR] = useState([]);
  const [areasCatalogo, setAreasCatalogo] = useState([]);
  const [poolUsuarios, setPoolUsuarios] = useState([]);
  const [savingModo, setSavingModo] = useState(false);
  const [loadingModo, setLoadingModo] = useState(true);

  // Integraciones
  const [integraciones, setIntegraciones] = useState([]);
  const [loadingInteg, setLoadingInteg] = useState(true);
  const [savingIntegId, setSavingIntegId] = useState(null);
  const [testingIntegId, setTestingIntegId] = useState(null);
  const [showAddInteg, setShowAddInteg] = useState(false);
  const [newIntegNombre, setNewIntegNombre] = useState('');
  const [newIntegTipo, setNewIntegTipo] = useState('endpoint');
  const [deletingIntegId, setDeletingIntegId] = useState(null);
  const [showKeyMap, setShowKeyMap] = useState({});
  const [regeneratingKeyId, setRegeneratingKeyId] = useState(null);
  const [confirmRegenId, setConfirmRegenId] = useState(null);
  // Mailing config
  const [mailingProyectoId, setMailingProyectoId] = useState('');
  const [mailingConfigs, setMailingConfigs] = useState([]);
  const [mailingSubs, setMailingSubs] = useState([]);
  const [loadingMailing, setLoadingMailing] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(null); // 'config_id:field'
  const [savingMailingId, setSavingMailingId] = useState(null);
  const [previewHeroUrl, setPreviewHeroUrl] = useState(null);
  const loadAsignaciones = useCallback(async () => {
    setLoadingArq(true);
    setLoadingModo(true);
    try {
      const [arqResp, modoResp] = await Promise.all([
        api.get('/entregas/config/arquitectos-batch'),
        api.get('/entregas/config/modo-asignacion'),
      ]);
      const d = arqResp.data;
      setProyectosConSubs(d.proyectos_con_subdivisiones || []);
      setProyectos((d.proyectos_con_subdivisiones || []).map(p => ({ id: p.id, nombre: p.nombre })));
      setAsignaciones(d.asignaciones || []);
      setUsuarios(d.arquitectos_disponibles || []);
      api.get('/entregas/config/entrega-masiva')
        .then(r => setMasivaMap(Object.fromEntries((r.data || []).map(m => [`${m.proyecto_id}-${m.subdivision_id || 'null'}`, !!m.es_masiva]))))
        .catch(() => toast.error('Error al cargar configuración de entregas masivas'));

      const mc = modoResp.data;
      setModoAsignacion(mc.config?.modo || 'proyecto_etapa');
      setAreasRR(mc.config?.areas_round_robin || []);
      setAreasCatalogo(mc.areas_funcionales || []);
      setPoolUsuarios(mc.pool_usuarios || []);
    } catch (err) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoadingArq(false);
      setLoadingModo(false);
    }
  }, []);

  const handleSaveModo = async (nuevoModo, nuevasAreas) => {
    setSavingModo(true);
    try {
      const resp = await api.put('/entregas/config/modo-asignacion', {
        modo: nuevoModo,
        areas_round_robin: nuevasAreas,
      });
      setModoAsignacion(nuevoModo);
      setAreasRR(nuevasAreas);
      setPoolUsuarios(resp.data.pool_usuarios || []);
      toast.success('Modo de asignación actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar modo');
    } finally {
      setSavingModo(false);
    }
  };

  const toggleArea = (areaId) => {
    const newAreas = areasRR.includes(areaId)
      ? areasRR.filter(id => id !== areaId)
      : [...areasRR, areaId];
    setAreasRR(newAreas);
    if (modoAsignacion === 'round_robin') {
      handleSaveModo('round_robin', newAreas);
    }
  };




  const loadDocumentos = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const resp = await api.get('/entregas/config/documentos-checklist');
      setDocumentos(resp.data || []);
    } catch (err) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    const tab = activeTab;
    if (loadedTabs.current[tab]) return;
    loadedTabs.current[tab] = true;

    if (tab === 'arquitectos') { loadAsignaciones(); }
    else if (tab === 'documentos') { loadDocumentos(); }
    else if (tab === 'notificaciones') { loadConfigNotif(); }
    else if (tab === 'almacenamiento') { loadConfigAlmac(); }
    else if (tab === 'checklist') { loadChecklist(); loadAllUsuarios(); }
    else if (tab === 'integraciones') { loadIntegraciones(); }
    else if (tab === 'mailing') { loadMailingConfig(); }
  }, [activeTab, loadAsignaciones, loadDocumentos]);

  const NOTIF_ORDEN = {
    'nueva_entrega_liberada': 1,
    'liberacion_vencida': 2,
    'inspeccion_vencida': 3,
    'observaciones_internas_vencidas': 4,
    'programacion_fechas_vencida': 5,
    'solicitud_reprogramacion': 6,
    'entrega_proxima_vencer': 7,
  };

  const loadConfigNotif = async () => {
    setLoadingNotif(true);
    try {
      const resp = await api.get('/entregas/config/notificaciones');
      const sorted = (resp.data || []).sort((a, b) => (NOTIF_ORDEN[a.clave] || 99) - (NOTIF_ORDEN[b.clave] || 99));
      setConfigNotif(sorted);
    } catch (err) {}
    finally { setLoadingNotif(false); }
  };

  const loadConfigAlmac = async () => {
    setLoadingAlmac(true);
    try {
      const resp = await api.get('/entregas/config/almacenamiento');
      setConfigAlmac(resp.data || {});
    } catch (err) {}
    finally { setLoadingAlmac(false); }
  };

  const handleToggleNotif = async (clave, activa) => {
    try {
      await api.put(`/entregas/config/notificaciones/${clave}`, { activa });
      setConfigNotif(prev => prev.map(c => c.clave === clave ? {...c, activa} : c));
      toast.success('Configuración actualizada');
    } catch (err) { toast.error('Error al actualizar'); }
  };

  const handleUpdateNotifParams = async (clave, parametros) => {
    try {
      await api.put(`/entregas/config/notificaciones/${clave}`, { parametros });
      setConfigNotif(prev => prev.map(c => c.clave === clave ? {...c, parametros} : c));
      toast.success('Parámetros actualizados');
    } catch (err) { toast.error('Error al actualizar'); }
  };

  const handleSaveAlmac = async () => {
    setSavingAlmac(true);
    try {
      await api.put('/entregas/config/almacenamiento', configAlmac);
      toast.success('Almacenamiento actualizado');
    } catch (err) { toast.error('Error al guardar'); }
    finally { setSavingAlmac(false); }
  };

  // Handlers Arquitectos — inline dropdown change
  const handleArqChange = async (proyectoId, proyectoNombre, subdivisionId, subdivisionNombre, nuevoArqId) => {
    const cellKey = `${proyectoId}-${subdivisionId || 'null'}`;
    setSavingArqCell(cellKey);
    try {
      const existing = asignaciones.find(a =>
        a.proyecto_id === proyectoId &&
        (subdivisionId ? a.subdivision_id === subdivisionId : !a.subdivision_id)
      );

      if (nuevoArqId === '__none__') {
        // Delete
        if (existing) {
          await api.delete(`/entregas/config/arquitectos-proyecto/${existing.id}`);
          toast.success('Asignación eliminada');
        }
      } else if (existing) {
        // Update
        const user = usuarios.find(u => u.id === nuevoArqId);
        await api.put(`/entregas/config/arquitectos-proyecto/${existing.id}`, {
          arquitecto_id: nuevoArqId,
          arquitecto_nombre: user?.nombre
        });
        toast.success('Asignación actualizada');
      } else {
        // Create
        const user = usuarios.find(u => u.id === nuevoArqId);
        await api.post('/entregas/config/arquitectos-proyecto', {
          proyecto_id: proyectoId,
          proyecto_nombre: proyectoNombre,
          subdivision_id: subdivisionId || null,
          subdivision_nombre: subdivisionNombre || null,
          arquitecto_id: nuevoArqId,
          arquitecto_nombre: user?.nombre
        });
        toast.success('Asignación creada');
      }
      // Reload
      const resp = await api.get('/entregas/config/arquitectos-proyecto');
      setAsignaciones(resp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar asignación');
    } finally {
      setSavingArqCell(null);
    }
  };

  const handleMasivaChange = async (proyectoId, subdivisionId, checked, etiqueta) => {
    const key = `${proyectoId}-${subdivisionId || 'null'}`;
    setSavingMasiva(key);
    try {
      await api.put('/entregas/config/entrega-masiva', {
        proyecto_id: proyectoId, subdivision_id: subdivisionId || null, es_masiva: checked,
      });
      setMasivaMap(prev => ({ ...prev, [key]: checked }));
      toast.success(checked ? `${etiqueta}: marcada como entrega masiva` : `${etiqueta}: entrega masiva desactivada`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar entrega masiva');
    } finally {
      setSavingMasiva(null);
    }
  };

  // Build hierarchical rows for the table
  const tablaFilas = React.useMemo(() => {
    const rows = [];
    const asigMap = {};
    asignaciones.forEach(a => {
      const key = `${a.proyecto_id}-${a.subdivision_id || 'null'}`;
      asigMap[key] = a;
    });

    for (const proy of proyectosConSubs) {
      const subs = proy.subdivisiones || [];
      const etapas = subs.filter(s => (s.tipo_subdivision || '').toLowerCase() === 'etapa');
      const bloques = subs.filter(s => (s.tipo_subdivision || '').toLowerCase() === 'bloque');

      if (etapas.length === 0 && bloques.length === 0) {
        // Project without subdivisions: single row
        const key = `${proy.id}-null`;
        rows.push({
          key, proyectoId: proy.id, proyectoNombre: proy.nombre,
          etapaNombre: null, bloqueNombre: null,
          subdivisionId: null, subdivisionNombre: null,
          asignacion: asigMap[key] || null,
          isEtapaRow: true, proyRowspan: 1, etapaRowspan: 0,
          showProyecto: true, showEtapa: false,
          masiva: { subdivisionId: null, rowSpan: 1 }
        });
      } else if (etapas.length === 0) {
        // Only blocks (no etapas) — list each block
        bloques.forEach((b, i) => {
          const key = `${proy.id}-${b.id}`;
          rows.push({
            key, proyectoId: proy.id, proyectoNombre: proy.nombre,
            etapaNombre: null, bloqueNombre: b.nombre,
            subdivisionId: b.id, subdivisionNombre: b.nombre,
            asignacion: asigMap[key] || null,
            isEtapaRow: false, proyRowspan: i === 0 ? bloques.length : 0, etapaRowspan: 0,
            showProyecto: i === 0, showEtapa: false,
            masiva: i === 0 ? { subdivisionId: null, rowSpan: bloques.length } : null
          });
        });
      } else {
        // Has etapas — build hierarchy
        let proyRowCount = 0;
        const etapaRows = [];
        for (const et of etapas) {
          const hijos = bloques.filter(b => b.padre_id === et.id);
          const etapaTotal = 1 + hijos.length; // etapa row + its block rows
          proyRowCount += etapaTotal;

          // Etapa-level row
          const etKey = `${proy.id}-${et.id}`;
          etapaRows.push({
            key: etKey, proyectoId: proy.id, proyectoNombre: proy.nombre,
            etapaNombre: et.nombre, bloqueNombre: '-',
            subdivisionId: et.id, subdivisionNombre: et.nombre,
            asignacion: asigMap[etKey] || null,
            isEtapaRow: true, etapaRowspan: etapaTotal,
            showEtapa: true,
            masiva: { subdivisionId: et.id, rowSpan: etapaTotal }
          });
          // Block rows under this etapa
          for (const bl of hijos) {
            const blKey = `${proy.id}-${bl.id}`;
            etapaRows.push({
              key: blKey, proyectoId: proy.id, proyectoNombre: proy.nombre,
              etapaNombre: et.nombre, bloqueNombre: bl.nombre,
              subdivisionId: bl.id, subdivisionNombre: bl.nombre,
              asignacion: asigMap[blKey] || null,
              isEtapaRow: false, etapaRowspan: 0,
              showEtapa: false
            });
          }
        }
        // Assign proyRowspan to first row
        etapaRows.forEach((r, i) => {
          r.showProyecto = i === 0;
          r.proyRowspan = i === 0 ? proyRowCount : 0;
        });
        rows.push(...etapaRows);
      }
    }
    return rows;
  }, [proyectosConSubs, asignaciones]);


  // Checklist Pre-Entrega
  const loadChecklist = async () => {
    setLoadingChecklist(true);
    try {
      const resp = await api.get('/entregas/config/checklist-preentrega');
      setChecklistItems(resp.data || []);
    } catch { }
    finally { setLoadingChecklist(false); }
  };

  const loadAllUsuarios = async () => {
    try {
      const resp = await api.get('/users');
      setAllUsuarios(resp.data || []);
    } catch { }
  };

  const handleCrearChecklistItem = async () => {
    if (!newCheckNombre.trim()) { toast.error('Nombre requerido'); return; }
    try {
      const respId = newCheckResponsable && newCheckResponsable !== 'none' ? newCheckResponsable : null;
      const responsable = respId ? allUsuarios.find(u => u.id === respId) : null;
      await api.post('/entregas/config/checklist-preentrega', {
        nombre: newCheckNombre.trim(),
        responsable_id: respId,
        responsable_nombre: responsable?.nombre || null,
        orden: checklistItems.length
      });
      toast.success('Actividad agregada');
      setShowAddChecklist(false);
      setNewCheckNombre('');
      setNewCheckResponsable('');
      loadChecklist();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear actividad');
    }
  };

  const handleEliminarChecklistItem = async (id) => {
    try {
      await api.delete(`/entregas/config/checklist-preentrega/${id}`);
      toast.success('Actividad eliminada');
      loadChecklist();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };


  // Handlers Documentos
  const handleCrearDocumento = async () => {
    if (!newDocNombre.trim()) { toast.error('Nombre requerido'); return; }
    try {
      await api.post('/entregas/config/documentos-checklist', {
        nombre: newDocNombre.trim(),
        obligatorio: newDocObligatorio,
        orden: documentos.length
      });
      toast.success('Documento agregado');
      setShowAddDoc(false);
      setNewDocNombre('');
      setNewDocObligatorio(true);
      loadDocumentos();
    } catch (err) {
      toast.error('Error al crear documento');
    }
  };

  const handleEliminarDocumento = async (id) => {
    try {
      await api.delete(`/entregas/config/documentos-checklist/${id}`);
      toast.success('Documento eliminado');
      loadDocumentos();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  // ── Integraciones ──
  const loadIntegraciones = async () => {
    setLoadingInteg(true);
    try {
      const resp = await api.get('/entregas/config/integraciones');
      setIntegraciones((resp.data || []).map(it => ({
        ...it,
        _url: it.url || '',
        _api_key: it.api_key || '',
        _activo: it.activo || false,
      })));
    } catch {
      setIntegraciones([]);
    }
    finally { setLoadingInteg(false); }
  };

  const updateIntegLocal = (id, field, value) => {
    setIntegraciones(prev => prev.map(it => it.id === id ? { ...it, [field]: value } : it));
  };

  const saveIntegracion = async (item) => {
    setSavingIntegId(item.id);
    try {
      await api.put(`/entregas/config/integraciones/${item.id}`, {
        url: item._url,
        api_key: item._api_key,
        activo: item._activo,
      });
      toast.success(`"${item.nombre}" guardado`);
      loadedTabs.current['integraciones'] = false;
      loadIntegraciones();
    } catch { toast.error('Error al guardar'); }
    finally { setSavingIntegId(null); }
  };

  const testIntegracion = async (item) => {
    setTestingIntegId(item.id);
    try {
      const resp = await api.post(`/entregas/config/integraciones/${item.id}/test`);
      const d = resp.data;
      if (d.ok) {
        toast.success(d.message);
      } else {
        toast.error(d.message);
      }
    } catch { toast.error('Error al probar conexión'); }
    finally { setTestingIntegId(null); }
  };

  const crearIntegracion = async () => {
    try {
      await api.post('/entregas/config/integraciones', {
        nombre: newIntegNombre.trim(),
        tipo: newIntegTipo,
      });
      toast.success('Integración creada');
      setShowAddInteg(false);
      setNewIntegNombre('');
      setNewIntegTipo('endpoint');
      loadedTabs.current['integraciones'] = false;
      loadIntegraciones();
    } catch { toast.error('Error al crear'); }
  };

  const eliminarIntegracion = async (id) => {
    try {
      await api.delete(`/entregas/config/integraciones/${id}`);
      toast.success('Integración eliminada');
      setDeletingIntegId(null);
      setIntegraciones(prev => prev.filter(it => it.id !== id));
    } catch { toast.error('Error al eliminar'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text || '');
    toast.success('Copiado al portapapeles');
  };

  const regenerarApiKey = async (item) => {
    setRegeneratingKeyId(item.id);
    try {
      const resp = await api.post(`/entregas/config/integraciones/${item.id}/regenerar-api-key`);
      const d = resp.data;
      if (d.ok) {
        updateIntegLocal(item.id, '_api_key', d.api_key);
        setShowKeyMap(prev => ({ ...prev, [item.id]: true }));
        toast.success(d.message);
      } else {
        toast.error(d.message || 'Error al regenerar');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al regenerar API Key');
    } finally {
      setRegeneratingKeyId(null);
      setConfirmRegenId(null);
    }
  };

  // ─── Mailing functions ───
  const loadMailingConfig = async () => {
    if (!mailingProyectoId) return;
    setLoadingMailing(true);
    try {
      const [confResp, subsResp] = await Promise.all([
        api.get(`/entregas/config/mailing?proyecto_id=${mailingProyectoId}`),
        api.get(`/entregas/config/mailing/subdivisiones?proyecto_id=${mailingProyectoId}`).catch(() => ({ data: [] })),
      ]);
      setMailingConfigs(confResp.data || []);
      setMailingSubs(subsResp.data || []);
    } catch { setMailingConfigs([]); setMailingSubs([]); }
    finally { setLoadingMailing(false); }
  };

  useEffect(() => {
    if (activeTab === 'mailing' && mailingProyectoId) loadMailingConfig();
  }, [mailingProyectoId, activeTab]);

  const ensureMailingConfig = async (subdivisionId = null) => {
    const existing = mailingConfigs.find(c =>
      !c.id.startsWith('_tmp_') && (subdivisionId ? c.subdivision_id === subdivisionId : !c.subdivision_id)
    );
    if (existing) return existing.id;
    const resp = await api.post('/entregas/config/mailing', {
      proyecto_id: mailingProyectoId,
      subdivision_id: subdivisionId || null,
    });
    const newConfig = resp.data;
    setMailingConfigs(prev => [...prev, newConfig]);
    return newConfig.id;
  };

  const uploadHero = async (subdivisionId, field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const key = `${subdivisionId || 'general'}:${field}`;
      setUploadingHero(key);
      try {
        const configId = await ensureMailingConfig(subdivisionId);
        const endpoint = field === 'hero_invitacion_url' ? 'hero-invitacion' : 'hero-confirmacion';
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/entregas/config/mailing/${configId}/${endpoint}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Imagen subida');
        await loadMailingConfig();
      } catch { toast.error('Error al subir imagen'); }
      finally { setUploadingHero(null); }
    };
    input.click();
  };

  const deleteHero = async (configId, field) => {
    const endpoint = field === 'hero_invitacion_url' ? 'hero-invitacion' : 'hero-confirmacion';
    try {
      await api.delete(`/entregas/config/mailing/${configId}/${endpoint}`);
      toast.success('Imagen eliminada');
      await loadMailingConfig();
    } catch { toast.error('Error al eliminar'); }
  };

  const getMailingRow = (subdivisionId) => {
    return mailingConfigs.find(c =>
      subdivisionId ? c.subdivision_id === subdivisionId : !c.subdivision_id
    );
  };

  const getInheritedUrl = (field) => {
    const general = mailingConfigs.find(c => !c.subdivision_id);
    return general?.[field] || null;
  };

  const updateMailingConfig = async (subdivisionId, field, value) => {
    const key = `${subdivisionId || 'general'}:${field}`;
    setSavingMailingId(key);
    try {
      const configId = await ensureMailingConfig(subdivisionId);
      await api.put(`/entregas/config/mailing/${configId}`, { [field]: value });
      setMailingConfigs(prev => prev.map(c =>
        c.id === configId ? { ...c, [field]: value } : c
      ));
      toast.success('Configuración actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSavingMailingId(null);
    }
  };

  const tabs = [
    { key: 'arquitectos', label: 'Arquitectos', icon: Users },
    { key: 'checklist', label: 'Checklist Pre-Entrega', icon: ClipboardCheck },
    { key: 'documentos', label: 'Documentos Cierre', icon: FileText },
    { key: 'integraciones', label: 'Integraciones', icon: Plug },
    { key: 'notificaciones', label: 'Notificaciones', icon: Bell },
    { key: 'almacenamiento', label: 'Almacenamiento', icon: HardDrive },
    { key: 'mailing', label: 'Mailing', icon: Mail },
  ];

  return (
    <div className="space-y-4" data-testid="entregas-configuracion">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Settings size={22} className="text-[#4E7A79]" />
        <h1 className="text-xl sm:text-2xl font-bold text-[#2D2D2D]">Configuración Entregas</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E8E8E7]">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'border-[#4E7A79] text-[#4E7A79]'
                  : 'border-transparent text-[#888] hover:text-[#5A5A5A]'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab: Arquitectos por Proyecto */}
      {activeTab === 'arquitectos' && (
        <div className="space-y-4">
          {/* Título + Radio buttons */}
          <Card className="border border-[#E8E8E7]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Users size={18} className="text-[#4E7A79]" />
                Asignación de Arquitectos para Entregas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingModo ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4">
                  <label
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all flex-1 ${modoAsignacion === 'round_robin' ? 'border-[#4E7A79] bg-[#4E7A79]/5' : 'border-[#E8E8E7] hover:border-[#D4C5B5]'}`}
                    data-testid="radio-round-robin"
                  >
                    <input
                      type="radio"
                      name="modo-asignacion"
                      value="round_robin"
                      checked={modoAsignacion === 'round_robin'}
                      onChange={() => handleSaveModo('round_robin', areasRR)}
                      className="accent-[#4E7A79]"
                      disabled={savingModo || readOnly}
                    />
                    <div>
                      <p className="text-sm font-medium text-[#2D2D2D]">Asignación por Round Robin</p>
                      <p className="text-[11px] text-[#888] mt-0.5">Distribución equitativa entre colaboradores de las áreas seleccionadas</p>
                    </div>
                  </label>
                  <label
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all flex-1 ${modoAsignacion === 'proyecto_etapa' ? 'border-[#4E7A79] bg-[#4E7A79]/5' : 'border-[#E8E8E7] hover:border-[#D4C5B5]'}`}
                    data-testid="radio-proyecto-etapa"
                  >
                    <input
                      type="radio"
                      name="modo-asignacion"
                      value="proyecto_etapa"
                      checked={modoAsignacion === 'proyecto_etapa'}
                      onChange={() => handleSaveModo('proyecto_etapa', areasRR)}
                      className="accent-[#4E7A79]"
                      disabled={savingModo || readOnly}
                    />
                    <div>
                      <p className="text-sm font-medium text-[#2D2D2D]">Asignación por Proyecto / Etapa</p>
                      <p className="text-[11px] text-[#888] mt-0.5">Asignación fija según la tabla de proyecto, etapa y bloque</p>
                    </div>
                  </label>
                </div>
              )}
              {savingModo && (
                <div className="flex items-center gap-2 text-xs text-[#5B7FA6]">
                  <Loader2 size={12} className="animate-spin" /> Guardando...
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sección Round Robin */}
          <Card className={`border border-[#E8E8E7] transition-opacity ${modoAsignacion !== 'round_robin' ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Zap size={16} className="text-[#5B7FA6]" />
                Round Robin — Áreas funcionales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingModo ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
                </div>
              ) : (<>
              <p className="text-[11px] text-[#888]">
                Seleccione las áreas funcionales cuyos colaboradores participarán en la asignación por Round Robin:
              </p>
              <div className="flex flex-wrap gap-2" data-testid="areas-rr-list">
                {areasCatalogo.map(area => {
                  const isSelected = areasRR.includes(area.id);
                  return (
                    <button
                      key={area.id}
                      onClick={() => toggleArea(area.id)}
                      disabled={savingModo || modoAsignacion !== 'round_robin' || readOnly}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        isSelected
                          ? 'bg-[#4E7A79] text-white border-[#4E7A79]'
                          : 'bg-white text-[#555] border-[#D4C5B5] hover:border-[#4E7A79] hover:text-[#4E7A79]'
                      }`}
                      data-testid={`area-chip-${area.id}`}
                    >
                      {isSelected && <Check size={12} className="inline mr-1" />}
                      {area.nombre}
                    </button>
                  );
                })}
              </div>
              {/* Pool preview */}
              {poolUsuarios.length > 0 && (
                <div className="mt-3 p-3 bg-[#F8F8F7] rounded-lg border border-[#E8E8E7]">
                  <p className="text-[11px] font-medium text-[#888] mb-2">
                    Pool de arquitectos ({poolUsuarios.length}):
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {poolUsuarios.map(u => (
                      <Badge key={u.id} variant="secondary" className="text-[11px] font-normal">
                        <User size={10} className="mr-1" />
                        {u.nombre}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {modoAsignacion === 'round_robin' && areasRR.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <Info size={12} /> Seleccione al menos un área funcional para activar Round Robin
                </p>
              )}
              </>)}
            </CardContent>
          </Card>

          {/* Sección Proyecto / Etapa */}
          <Card className={`border border-[#E8E8E7] transition-opacity ${modoAsignacion !== 'proyecto_etapa' ? 'opacity-50 pointer-events-none' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Building2 size={16} className="text-[#5B7FA6]" />
                  Proyecto / Etapa
                  <Badge variant="secondary">{asignaciones.length}</Badge>
                </CardTitle>
              </div>
            </CardHeader>
            {/* Cascade hint */}
            <div className="flex items-center gap-2 px-5 py-2 bg-[#F0F6FC] border-y border-[#E8E8E7] text-[11px] text-[#5B7FA6]" data-testid="cascade-hint">
              <Info size={14} className="shrink-0" />
              Al asignar un arquitecto a una etapa, se aplicará automáticamente a los bloques que no tengan arquitecto asignado.
            </div>
          <CardContent className="p-0">
            {loadingArq ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
              </div>
            ) : proyectosConSubs.length === 0 ? (
              <p className="text-sm text-[#888] text-center py-8">
                No hay proyectos configurados.
              </p>
            ) : (
              <div data-testid="arq-table-container">
                <table className="w-full text-[13px] border-collapse">
                  <thead className="bg-[#F8F8F7] sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-3.5 py-2.5 font-medium text-xs text-[#888] border-b border-[#E8E8E7]" style={{width:'20%'}}>Proyecto</th>
                      <th className="text-left px-3.5 py-2.5 font-medium text-xs text-[#888] border-b border-[#E8E8E7]" style={{width:'16%'}}>Etapa</th>
                      <th className="text-left px-3.5 py-2.5 font-medium text-xs text-[#888] border-b border-[#E8E8E7]" style={{width:'13%'}}>Bloque</th>
                      <th className="text-left px-3.5 py-2.5 font-medium text-xs text-[#888] border-b border-[#E8E8E7]" style={{width:'35%'}}>Arquitecto Asignado</th>
                      <th className="text-center px-3.5 py-2.5 font-medium text-xs text-[#888] border-b border-l border-[#E8E8E7]" style={{width:'16%'}} data-testid="col-masiva">¿Masiva?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tablaFilas.map((row, idx) => {
                      const currentArqId = row.asignacion?.arquitecto_id || '__none__';
                      const isSaving = savingArqCell === row.key;
                      const isFirstOfProject = row.showProyecto;
                      return (
                        <tr
                          key={row.key}
                          className={`hover:bg-[#FAFAF9] ${row.isEtapaRow ? 'bg-[#FAFAF9]' : ''} ${isFirstOfProject && idx > 0 ? 'border-t-2 border-[#E8E8E7]' : ''}`}
                          data-testid={`arq-row-${row.key}`}
                        >
                          {row.showProyecto && (
                            <td
                              className="px-3.5 py-2 font-medium border-r border-[#E8E8E7] align-top"
                              rowSpan={row.proyRowspan}
                            >
                              <div className="flex items-center gap-1.5 sticky top-10 bg-inherit">
                                <Building2 size={14} className="text-[#5B7FA6] shrink-0" />
                                <span className="truncate">{row.proyectoNombre}</span>
                              </div>
                            </td>
                          )}
                          {row.showEtapa && row.etapaRowspan > 0 && (
                            <td
                              className="px-3.5 py-2 text-[#555] border-r border-[#E8E8E7] align-top"
                              rowSpan={row.etapaRowspan}
                            >
                              {row.etapaNombre || '-'}
                            </td>
                          )}
                          {/* For single-row projects without etapas, show etapa cell */}
                          {row.showProyecto && row.etapaRowspan === 0 && !row.showEtapa && (
                            <td className="px-3.5 py-2 text-[#999] border-r border-[#E8E8E7]">-</td>
                          )}
                          <td className="px-3.5 py-2 text-[#555] border-r border-[#E8E8E7]">
                            {row.bloqueNombre || '-'}
                          </td>
                          <td className="px-3.5 py-2">
                            <div className="flex items-center gap-2">
                              <select
                                className="w-full px-2.5 py-1.5 border border-[#D4C5B5] rounded-md text-xs bg-white text-[#2D2D2D] focus:outline-none focus:border-[#5B7FA6] focus:ring-2 focus:ring-[#5B7FA6]/15 cursor-pointer disabled:opacity-50"
                                value={currentArqId}
                                disabled={isSaving || readOnly}
                                onChange={e => handleArqChange(
                                  row.proyectoId, row.proyectoNombre,
                                  row.subdivisionId, row.subdivisionNombre,
                                  e.target.value
                                )}
                                data-testid={`select-arq-${row.key}`}
                              >
                                <option value="__none__" className="text-[#999]">Ninguno</option>
                                {usuarios.map(u => (
                                  <option key={u.id} value={u.id}>{u.nombre}</option>
                                ))}
                              </select>
                              {isSaving && <Loader2 size={14} className="animate-spin text-[#5B7FA6] shrink-0" />}
                            </div>
                          </td>
                          {row.masiva && (
                            <MasivaCell
                              etiqueta={row.masiva.subdivisionId ? `${row.proyectoNombre} · ${row.etapaNombre}` : row.proyectoNombre}
                              rowKey={`${row.proyectoId}-${row.masiva.subdivisionId || 'null'}`}
                              rowSpan={row.masiva.rowSpan}
                              checked={!!masivaMap[`${row.proyectoId}-${row.masiva.subdivisionId || 'null'}`]}
                              saving={savingMasiva === `${row.proyectoId}-${row.masiva.subdivisionId || 'null'}`}
                              disabled={readOnly || !!savingMasiva}
                              onChange={(checked) => handleMasivaChange(
                                row.proyectoId, row.masiva.subdivisionId, checked,
                                row.masiva.subdivisionId ? `${row.proyectoNombre} · ${row.etapaNombre}` : row.proyectoNombre
                              )}
                            />
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Tab: Checklist Pre-Entrega */}
      {activeTab === 'checklist' && (
        <Card className="border border-[#E8E8E7]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <ClipboardCheck size={18} className="text-[#4E7A79]" />
                Actividades de Aseguramiento Administrativo
                <Badge variant="secondary">{checklistItems.length}</Badge>
              </CardTitle>
              <Button
                size="sm"
                onClick={() => setShowAddChecklist(true)}
                className="bg-[#4E7A79] hover:bg-[#3d6362] text-white text-xs"
                data-testid="btn-nueva-actividad-checklist"
                style={{ display: readOnly ? 'none' : undefined }}
              >
                <Plus size={14} className="mr-1" />
                Agregar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingChecklist ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
              </div>
            ) : checklistItems.length === 0 ? (
              <p className="text-sm text-[#888] text-center py-8">
                No hay actividades configuradas. Al transicionar una entrega a inspección, se copiará el listado vigente.
              </p>
            ) : (
              <div className="space-y-2">
                {checklistItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 border border-[#E8E8E7] rounded-lg hover:bg-[#FAFAFA]" data-testid={`config-checklist-item-${item.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <ClipboardCheck size={14} className="text-[#4E7A79] flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium block truncate">{item.nombre}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <User size={10} className="text-[#aaa]" />
                          <span className="text-xs text-[#888]">
                            {item.responsable_nombre || 'Cualquier usuario'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEliminarChecklistItem(item.id)}
                      className="text-red-400 hover:text-red-600 h-7 w-7 p-0 flex-shrink-0"
                      data-testid={`btn-eliminar-checklist-${item.id}`}
                      style={{ display: readOnly ? 'none' : undefined }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Documentos Checklist */}
      {activeTab === 'documentos' && (
        <Card className="border border-[#E8E8E7]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <FileText size={18} className="text-[#4E7A79]" />
                Documentos Requeridos para Cierre
                <Badge variant="secondary">{documentos.length}</Badge>
              </CardTitle>
              <Button
                size="sm"
                onClick={() => setShowAddDoc(true)}
                className="bg-[#4E7A79] hover:bg-[#3d6362] text-white text-xs"
                data-testid="btn-nuevo-documento"
                style={{ display: readOnly ? 'none' : undefined }}
              >
                <Plus size={14} className="mr-1" />
                Agregar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingDocs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
              </div>
            ) : documentos.length === 0 ? (
              <p className="text-sm text-[#888] text-center py-8">
                No hay documentos configurados para el checklist de cierre.
              </p>
            ) : (
              <div className="space-y-2">
                {documentos.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 border border-[#E8E8E7] rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-[#4E7A79]" />
                      <span className="text-sm">{d.nombre}</span>
                      {d.obligatorio && <Badge variant="outline" className="text-[10px] border-red-300 text-red-500">Obligatorio</Badge>}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEliminarDocumento(d.id)}
                      className="text-red-400 hover:text-red-600 h-7 w-7 p-0"
                      style={{ display: readOnly ? 'none' : undefined }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Notificaciones */}
      {activeTab === 'notificaciones' && (
        <Card className="border border-[#E8E8E7]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Bell size={18} className="text-[#4E7A79]" />
              Configuración de Notificaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingNotif ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
              </div>
            ) : configNotif.length === 0 ? (
              <p className="text-sm text-[#888] text-center py-8">No hay configuraciones de notificación disponibles.</p>
            ) : (
              <div className="space-y-4">
                {configNotif.map(c => (
                  <div key={c.clave} className="p-4 border border-[#E8E8E7] rounded-lg" data-testid={`notif-${c.clave}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-[#2D2D2D]">{c.nombre}</p>
                        <p className="text-xs text-[#888]">{c.descripcion}</p>
                      </div>
                      <Switch
                        checked={c.activa}
                        onCheckedChange={(v) => handleToggleNotif(c.clave, v)}
                        data-testid={`switch-notif-${c.clave}`}
                        disabled={readOnly}
                      />
                    </div>
                    {c.clave === 'entrega_proxima_vencer' && c.activa && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E8E8E7]">
                        <Label className="text-xs text-[#5A5A5A] whitespace-nowrap">Días de aviso anticipado:</Label>
                        <Input
                          type="number"
                          min={1}
                          max={90}
                          defaultValue={c.parametros?.dias_antes || 15}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value) || 15;
                            if (val !== (c.parametros?.dias_antes || 15)) {
                              handleUpdateNotifParams(c.clave, { ...c.parametros, dias_antes: val });
                            }
                          }}
                          className="w-20 h-8 text-sm"
                          data-testid="input-dias-aviso"
                          disabled={readOnly}
                        />
                      </div>
                    )}
                    {['liberacion_vencida', 'inspeccion_vencida', 'observaciones_internas_vencidas'].includes(c.clave) && c.activa && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E8E8E7]">
                        <Label className="text-xs text-[#5A5A5A] whitespace-nowrap">Horas de vencimiento:</Label>
                        <Input
                          type="number"
                          min={1}
                          max={720}
                          defaultValue={c.parametros?.horas || (c.clave === 'observaciones_internas_vencidas' ? 72 : 48)}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value) || 48;
                            const current = c.parametros?.horas || (c.clave === 'observaciones_internas_vencidas' ? 72 : 48);
                            if (val !== current) {
                              handleUpdateNotifParams(c.clave, { ...c.parametros, horas: val });
                            }
                          }}
                          className="w-20 h-8 text-sm"
                          data-testid={`input-horas-${c.clave}`}
                          disabled={readOnly}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Integraciones */}
      {activeTab === 'integraciones' && (
        <Card className="border border-[#E8E8E7]" data-testid="tab-integraciones-content">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Plug size={18} className="text-[#4E7A79]" /> CRM Inmobiliario
                </CardTitle>
                <p className="text-xs text-[#888] mt-1">Configure las APIs de integración con su CRM inmobiliario.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowAddInteg(true)} data-testid="btn-add-integ" style={{ display: readOnly ? 'none' : undefined }}>
                <Plus size={14} className="mr-1" /> Agregar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingInteg ? (
              <div className="flex justify-center py-10">
                <Loader2 size={22} className="animate-spin text-[#4E7A79]" />
              </div>
            ) : integraciones.length === 0 ? (
              <div className="text-center py-10 text-sm text-[#888]">
                No hay integraciones configuradas. Haga clic en "+ Agregar" para crear una.
              </div>
            ) : (
              integraciones.map(item => (
                <div
                  key={item.id}
                  className="border border-[#E8E8E7] rounded-lg p-4 space-y-3"
                  data-testid={`integ-card-${item.id}`}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-[#2D2D2D]">{item.nombre}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        item.tipo === 'endpoint'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'bg-purple-50 text-purple-700 border border-purple-200'
                      }`}>
                        {item.tipo === 'endpoint' ? 'Endpoint' : 'Webhook'}
                      </span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <span className={`text-[11px] font-medium ${item._activo ? 'text-green-600' : 'text-[#aaa]'}`}>
                        {item._activo ? 'Activo' : 'Inactivo'}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={item._activo}
                        onClick={() => updateIntegLocal(item.id, '_activo', !item._activo)}
                        disabled={readOnly}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          item._activo ? 'bg-green-500' : 'bg-gray-300'
                        } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`integ-switch-${item.id}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          item._activo ? 'translate-x-[18px]' : 'translate-x-[3px]'
                        }`} />
                      </button>
                    </label>
                  </div>

                  {/* URL */}
                  <div className="space-y-1">
                    <label className="text-[10px] text-[#888] font-medium uppercase tracking-wide">URL *</label>
                    <Input
                      placeholder="https://..."
                      value={item._url}
                      onChange={e => updateIntegLocal(item.id, '_url', e.target.value)}
                      className="text-sm font-mono"
                      data-testid={`integ-url-${item.id}`}
                      disabled={readOnly}
                    />
                  </div>

                  {/* API Key */}
                  <div className="space-y-1">
                    <label className="text-[10px] text-[#888] font-medium uppercase tracking-wide">API Key *</label>
                    <div className="flex gap-1">
                      <div className="relative flex-1">
                        <Input
                          type={showKeyMap[item.id] ? 'text' : 'password'}
                          placeholder="Bearer token"
                          value={item._api_key}
                          onChange={e => updateIntegLocal(item.id, '_api_key', e.target.value)}
                          className="text-sm font-mono pr-16"
                          data-testid={`integ-apikey-${item.id}`}
                          disabled={readOnly || item.tipo === 'webhook'}
                        />
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5">
                          <Button
                            type="button" variant="ghost" size="icon"
                            className="h-6 w-6"
                            onClick={() => setShowKeyMap(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                            title={showKeyMap[item.id] ? 'Ocultar' : 'Mostrar'}
                          >
                            {showKeyMap[item.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                          </Button>
                          <Button
                            type="button" variant="ghost" size="icon"
                            className="h-6 w-6"
                            onClick={() => copyToClipboard(item._api_key)}
                            title="Copiar"
                          >
                            <Copy size={13} />
                          </Button>
                        </div>
                      </div>
                      {item.tipo === 'webhook' && !readOnly && (
                        confirmRegenId === item.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="destructive" size="sm" className="h-9 text-xs whitespace-nowrap"
                              disabled={regeneratingKeyId === item.id}
                              onClick={() => regenerarApiKey(item)}
                              data-testid={`integ-regen-confirm-${item.id}`}
                            >
                              {regeneratingKeyId === item.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
                              Confirmar
                            </Button>
                            <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => setConfirmRegenId(null)}>
                              No
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="outline" size="sm" className="h-9 text-xs whitespace-nowrap"
                            onClick={() => setConfirmRegenId(item.id)}
                            data-testid={`integ-regen-${item.id}`}
                          >
                            <RefreshCw size={13} className="mr-1" /> Regenerar
                          </Button>
                        )
                      )}
                    </div>
                    {item.tipo === 'webhook' && (
                      <p className="text-[10px] text-[#aaa] mt-0.5">Gestionada automáticamente. Use "Regenerar" para obtener una nueva key.</p>
                    )}
                  </div>

                  {/* Actions */}
                  {!readOnly && (
                  <div className="flex items-center justify-end gap-2 pt-1 border-t border-[#f0f0f0]">
                    {deletingIntegId === item.id ? (
                      <div className="flex items-center gap-2 mr-auto">
                        <span className="text-xs text-red-600">¿Eliminar "{item.nombre}"?</span>
                        <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => eliminarIntegracion(item.id)}>
                          Sí, eliminar
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDeletingIntegId(null)}>
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost" size="sm"
                        className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 mr-auto"
                        onClick={() => setDeletingIntegId(item.id)}
                        data-testid={`integ-delete-${item.id}`}
                      >
                        <Trash2 size={13} className="mr-1" /> Eliminar
                      </Button>
                    )}
                    <Button
                      variant="outline" size="sm" className="text-xs"
                      disabled={!item._url?.trim() || !item._api_key?.trim() || testingIntegId === item.id}
                      onClick={() => testIntegracion(item)}
                      data-testid={`integ-test-${item.id}`}
                    >
                      {testingIntegId === item.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Zap size={13} className="mr-1" />}
                      Probar
                    </Button>
                    <Button
                      size="sm" className="text-xs bg-[#4E7A79] hover:bg-[#3d6261]"
                      disabled={!item._url?.trim() || !item._api_key?.trim() || savingIntegId === item.id}
                      onClick={() => saveIntegracion(item)}
                      data-testid={`integ-save-${item.id}`}
                    >
                      {savingIntegId === item.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />}
                      Guardar
                    </Button>
                  </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal: Nueva Integración */}
      {showAddInteg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]" onClick={() => setShowAddInteg(false)}>
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-[#2D2D2D]">Nueva Integración</h3>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] text-[#888] font-medium uppercase tracking-wide">Nombre *</label>
                <Input
                  placeholder="Ej: Consultar Entrega"
                  value={newIntegNombre}
                  onChange={e => setNewIntegNombre(e.target.value)}
                  autoFocus
                  data-testid="new-integ-nombre"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-[#888] font-medium uppercase tracking-wide">Tipo *</label>
                <Select value={newIntegTipo} onValueChange={setNewIntegTipo}>
                  <SelectTrigger className="h-9 text-sm" data-testid="new-integ-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="endpoint">Endpoint</SelectItem>
                    <SelectItem value="webhook">Webhook</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowAddInteg(false); setNewIntegNombre(''); }}>
                Cancelar
              </Button>
              <Button
                size="sm" className="bg-[#4E7A79] hover:bg-[#3d6261]"
                disabled={!newIntegNombre.trim()}
                onClick={crearIntegracion}
                data-testid="btn-crear-integ"
              >
                Crear
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Almacenamiento */}
      {activeTab === 'almacenamiento' && (
        <Card className="border border-[#E8E8E7]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <HardDrive size={18} className="text-[#4E7A79]" />
              Proveedor de Almacenamiento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAlmac ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <Label className="text-sm">Proveedor Activo</Label>
                  <Select
                    value={configAlmac.proveedor || 'supabase'}
                    onValueChange={(v) => setConfigAlmac(prev => ({ ...prev, proveedor: v }))}
                    disabled={readOnly}
                  >
                    <SelectTrigger className="mt-1 w-full max-w-xs" data-testid="sel-proveedor-almac">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supabase">Supabase Storage</SelectItem>
                      <SelectItem value="aws_s3">AWS S3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(configAlmac.proveedor || 'supabase') === 'supabase' && (
                  <div>
                    <Label className="text-sm">Nombre del Bucket</Label>
                    <Input
                      value={configAlmac.supabase_bucket || 'Entregas'}
                      onChange={(e) => setConfigAlmac(prev => ({ ...prev, supabase_bucket: e.target.value }))}
                      className="mt-1 max-w-xs"
                      data-testid="input-supabase-bucket"
                      disabled={readOnly}
                    />
                  </div>
                )}

                {configAlmac.proveedor === 'aws_s3' && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">Modo de Autenticación AWS</Label>
                      <Select
                        value={configAlmac.aws_auth_mode || 'access_key'}
                        onValueChange={(v) => setConfigAlmac(prev => ({ ...prev, aws_auth_mode: v }))}
                        disabled={readOnly}
                      >
                        <SelectTrigger className="mt-1 w-full max-w-xs" data-testid="sel-aws-auth-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="access_key">Access Key + Secret (simple)</SelectItem>
                          <SelectItem value="iam_role">IAM Role + External ID (recomendado)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">S3 Bucket</Label>
                      <Input
                        value={configAlmac.aws_s3_bucket || ''}
                        onChange={(e) => setConfigAlmac(prev => ({ ...prev, aws_s3_bucket: e.target.value }))}
                        className="mt-1 max-w-xs"
                        placeholder="mi-bucket-entregas"
                        data-testid="input-s3-bucket"
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Región</Label>
                      <Input
                        value={configAlmac.aws_s3_region || ''}
                        onChange={(e) => setConfigAlmac(prev => ({ ...prev, aws_s3_region: e.target.value }))}
                        className="mt-1 max-w-xs"
                        placeholder="us-east-1"
                        data-testid="input-s3-region"
                        disabled={readOnly}
                      />
                    </div>

                    {(configAlmac.aws_auth_mode || 'access_key') === 'access_key' && (
                      <>
                        <div>
                          <Label className="text-sm">Access Key ID</Label>
                          <Input
                            value={configAlmac.aws_access_key_id || ''}
                            onChange={(e) => setConfigAlmac(prev => ({ ...prev, aws_access_key_id: e.target.value }))}
                            className="mt-1 max-w-xs"
                            placeholder="AKIAIOSFODNN7EXAMPLE"
                            data-testid="input-s3-access-key"
                            disabled={readOnly}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Secret Access Key</Label>
                          <Input
                            type="password"
                            value={configAlmac.aws_secret_access_key || ''}
                            onChange={(e) => setConfigAlmac(prev => ({ ...prev, aws_secret_access_key: e.target.value }))}
                            className="mt-1 max-w-xs"
                            placeholder="••••••••"
                            data-testid="input-s3-secret-key"
                            disabled={readOnly}
                          />
                        </div>
                      </>
                    )}

                    {configAlmac.aws_auth_mode === 'iam_role' && (
                      <>
                        <div>
                          <Label className="text-sm">IAM Role ARN</Label>
                          <Input
                            value={configAlmac.aws_iam_role_arn || ''}
                            onChange={(e) => setConfigAlmac(prev => ({ ...prev, aws_iam_role_arn: e.target.value }))}
                            className="mt-1 max-w-xs"
                            placeholder="arn:aws:iam::123456789012:role/MiRol"
                            data-testid="input-s3-role-arn"
                            disabled={readOnly}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">External ID</Label>
                          <Input
                            value={configAlmac.aws_external_id || ''}
                            onChange={(e) => setConfigAlmac(prev => ({ ...prev, aws_external_id: e.target.value }))}
                            className="mt-1 max-w-xs"
                            placeholder="ExternalID-123"
                            data-testid="input-s3-external-id"
                            disabled={readOnly}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-sm">Directorio raíz (prefijo)</Label>
                  <Input
                    value={configAlmac.root_prefix || ''}
                    onChange={(e) => setConfigAlmac(prev => ({ ...prev, root_prefix: e.target.value }))}
                    className="mt-1 max-w-xs"
                    placeholder="entregas/produccion"
                    data-testid="input-root-prefix"
                    disabled={readOnly}
                  />
                  <p className="text-xs text-[#888] mt-1">Opcional. Los archivos se almacenarán dentro de este directorio.</p>
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    onClick={async () => {
                      setTestingAlmac(true);
                      try {
                        const resp = await api.post('/entregas/config/almacenamiento/test', configAlmac);
                        if (resp.data?.ok) {
                          toast.success(resp.data.message);
                        } else {
                          toast.error(resp.data?.message || 'Error de conexión');
                        }
                      } catch (err) {
                        toast.error('Error al probar la conexión');
                      } finally {
                        setTestingAlmac(false);
                      }
                    }}
                    disabled={testingAlmac || readOnly}
                    variant="outline"
                    className="border-[#4E7A79] text-[#4E7A79] hover:bg-[#4E7A79]/5"
                    data-testid="btn-test-almac"
                  >
                    {testingAlmac ? <Loader2 size={14} className="animate-spin mr-1" /> : <Zap size={14} className="mr-1" />}
                    Probar Conexión
                  </Button>

                  <Button
                    onClick={handleSaveAlmac}
                    disabled={savingAlmac || readOnly}
                    className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
                    data-testid="btn-guardar-almac"
                    style={{ display: readOnly ? 'none' : undefined }}
                  >
                    {savingAlmac ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                    Guardar Configuración
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Mailing */}
      {activeTab === 'mailing' && (
        <Card className="border border-[#E8E8E7]" data-testid="tab-content-mailing">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#4E7A79]">Configuraci&oacute;n de Correos por Proyecto</CardTitle>
            <p className="text-xs text-[#888] mt-1">Configure remitentes, destinatarios, cantidad de invitados e im&aacute;genes Hero de los correos de Entrega por proyecto y etapa.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selector de proyecto */}
            <div className="max-w-xs">
              <Label className="text-sm">Proyecto</Label>
              <Select value={mailingProyectoId} onValueChange={(v) => setMailingProyectoId(v)}>
                <SelectTrigger className="mt-1" data-testid="sel-mailing-proyecto">
                  <SelectValue placeholder="Seleccione un proyecto" />
                </SelectTrigger>
                <SelectContent>
                  {proyectos.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {mailingProyectoId && loadingMailing && (
              <div className="flex items-center gap-2 text-sm text-[#888] py-4">
                <Loader2 size={16} className="animate-spin" /> Cargando configuraci&oacute;n...
              </div>
            )}

            {mailingProyectoId && !loadingMailing && (
              <div className="space-y-4">
                {(() => {
                  const config = getMailingRow(null);
                  const rows = [{ label: 'General (todo el proyecto)', subdivisionId: null, config }];
                  mailingSubs.forEach(sub => {
                    rows.push({ label: sub.nombre, subdivisionId: sub.id, config: getMailingRow(sub.id) });
                  });
                  return rows.map((row) => {
                    const heroInv = row.config?.hero_invitacion_url;
                    const heroConf = row.config?.hero_confirmacion_url;
                    const inheritedInv = !heroInv && row.subdivisionId ? getInheritedUrl('hero_invitacion_url') : null;
                    const inheritedConf = !heroConf && row.subdivisionId ? getInheritedUrl('hero_confirmacion_url') : null;

                    // Configuraciones de remitente/destinatarios
                    const remitente = row.config?.remitente_correo || '';
                    const enviarTitular = row.config?.enviar_titular ?? true;
                    const copiarCoprop = row.config?.copiar_copropietarios ?? false;
                    const maxInvitados = row.config?.max_invitados ?? 1;
                    const tokenExpiraHoras = row.config?.token_expira_horas ?? 24;

                    return (
                      <Card key={row.subdivisionId || 'general'} className="border border-[#e8e8e7]">
                        <CardHeader className="pb-2 pt-3 px-4">
                          <div className="flex items-center gap-2">
                            {row.subdivisionId ? <ChevronRight size={14} className="text-[#888]" /> : <Building2 size={14} className="text-[#4E7A79]" />}
                            <CardTitle className="text-sm font-semibold">{row.label}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="px-4 pb-3 space-y-4">
                          {/* Remitente y Destinatarios */}
                          <div className="space-y-3" data-testid={`mailing-config-${row.subdivisionId || 'general'}`}>
                            <p className="text-xs font-semibold text-[#4E7A79] uppercase tracking-wide">Remitente y Destinatarios</p>

                            <div>
                              <Label className="text-xs text-[#888]">Remitente del correo</Label>
                              <div className="flex gap-2 mt-1">
                                <Input
                                  value={remitente}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (row.config?.id) {
                                      setMailingConfigs(prev => prev.map(c =>
                                        c.id === row.config.id ? { ...c, remitente_correo: val } : c
                                      ));
                                    } else {
                                      // Crear placeholder local para edición antes de guardar
                                      setMailingConfigs(prev => [...prev, {
                                        id: `_tmp_${row.subdivisionId || 'general'}`,
                                        proyecto_id: mailingProyectoId,
                                        subdivision_id: row.subdivisionId || null,
                                        remitente_correo: val,
                                      }]);
                                    }
                                  }}
                                  placeholder="Postventa Inmobiliaria <sac_dev@imagina.pe>"
                                  className="flex-1 text-sm"
                                  disabled={readOnly}
                                  data-testid={`input-remitente-${row.subdivisionId || 'general'}`}
                                />
                                {!readOnly && (
                                  <Button
                                    size="sm" variant="outline" className="text-xs h-9 px-3"
                                    disabled={savingMailingId === `${row.subdivisionId || 'general'}:remitente_correo`}
                                    onClick={() => updateMailingConfig(row.subdivisionId, 'remitente_correo', remitente)}
                                    data-testid={`btn-save-remitente-${row.subdivisionId || 'general'}`}
                                  >
                                    {savingMailingId === `${row.subdivisionId || 'general'}:remitente_correo`
                                      ? <Loader2 size={12} className="animate-spin" />
                                      : <Save size={12} />}
                                  </Button>
                                )}
                              </div>
                              <p className="text-[10px] text-[#aaa] mt-1">Formato: Nombre &lt;correo@dominio.com&gt;</p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="flex items-center justify-between border border-[#e8e8e7] rounded-lg px-3 py-2.5">
                                <div>
                                  <p className="text-xs font-medium text-[#333]">Enviar al Titular</p>
                                  <p className="text-[10px] text-[#888]">Correo al propietario principal</p>
                                </div>
                                <Switch
                                  checked={enviarTitular}
                                  onCheckedChange={(v) => updateMailingConfig(row.subdivisionId, 'enviar_titular', v)}
                                  disabled={readOnly}
                                  data-testid={`switch-enviar-titular-${row.subdivisionId || 'general'}`}
                                />
                              </div>
                              <div className="flex items-center justify-between border border-[#e8e8e7] rounded-lg px-3 py-2.5">
                                <div>
                                  <p className="text-xs font-medium text-[#333]">CC Copropietarios</p>
                                  <p className="text-[10px] text-[#888]">Copia a los copropietarios</p>
                                </div>
                                <Switch
                                  checked={copiarCoprop}
                                  onCheckedChange={(v) => updateMailingConfig(row.subdivisionId, 'copiar_copropietarios', v)}
                                  disabled={readOnly}
                                  data-testid={`switch-copiar-coprop-${row.subdivisionId || 'general'}`}
                                />
                              </div>
                              <div className="flex items-center justify-between border border-[#e8e8e7] rounded-lg px-3 py-2.5">
                                <div>
                                  <p className="text-xs font-medium text-[#333]">M&aacute;x. Invitados</p>
                                  <p className="text-[10px] text-[#888]">0 = sin invitados</p>
                                </div>
                                <Select
                                  value={String(maxInvitados)}
                                  onValueChange={(v) => updateMailingConfig(row.subdivisionId, 'max_invitados', parseInt(v))}
                                  disabled={readOnly}
                                >
                                  <SelectTrigger className="w-16 h-8 text-xs" data-testid={`sel-max-invitados-${row.subdivisionId || 'general'}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex items-center justify-between border border-[#e8e8e7] rounded-lg px-3 py-2.5">
                                <div>
                                  <p className="text-xs font-medium text-[#333]">Expiraci&oacute;n enlace</p>
                                  <p className="text-[10px] text-[#888]">Horas de vigencia</p>
                                </div>
                                <Select
                                  value={String(tokenExpiraHoras)}
                                  onValueChange={(v) => updateMailingConfig(row.subdivisionId, 'token_expira_horas', parseInt(v))}
                                  disabled={readOnly}
                                >
                                  <SelectTrigger className="w-16 h-8 text-xs" data-testid={`sel-token-expira-${row.subdivisionId || 'general'}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[24,48,72,96,120,144,168].map(n => (
                                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          {/* Separador */}
                          <div className="border-t border-dashed border-[#e0e0e0]" />

                          {/* Imágenes Hero */}
                          <div>
                            <p className="text-xs font-semibold text-[#4E7A79] uppercase tracking-wide mb-3">Im&aacute;genes Hero</p>
                            <div className="grid grid-cols-2 gap-4">
                            {/* Hero Invitación */}
                            <div>
                              <p className="text-xs font-medium text-[#888] mb-2">Hero Invitaci&oacute;n Entrega</p>
                              {heroInv ? (
                                <div className="space-y-2">
                                  <div className="relative group cursor-pointer" onClick={() => setPreviewHeroUrl(heroInv)}>
                                    <img src={heroInv} alt="Hero invitación" className="w-full h-24 object-cover rounded border" data-testid={`hero-inv-preview-${row.subdivisionId || 'general'}`} />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded flex items-center justify-center">
                                      <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  </div>
                                  {!readOnly && (
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => uploadHero(row.subdivisionId, 'hero_invitacion_url')}
                                        disabled={!!uploadingHero} data-testid={`btn-change-hero-inv-${row.subdivisionId || 'general'}`}>
                                        {uploadingHero === `${row.subdivisionId || 'general'}:hero_invitacion_url` ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />} Cambiar
                                      </Button>
                                      <Button size="sm" variant="ghost" className="text-xs h-7 text-red-500" onClick={() => deleteHero(row.config.id, 'hero_invitacion_url')}
                                        data-testid={`btn-del-hero-inv-${row.subdivisionId || 'general'}`}>
                                        <Trash2 size={12} className="mr-1" /> Eliminar
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {inheritedInv ? (
                                    <div>
                                      <div className="relative group cursor-pointer" onClick={() => setPreviewHeroUrl(inheritedInv)}>
                                        <img src={inheritedInv} alt="Heredada" className="w-full h-24 object-cover rounded border opacity-40" />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded flex items-center justify-center">
                                          <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                      </div>
                                      <p className="text-xs text-[#aaa] italic mt-1">(hereda: General)</p>
                                    </div>
                                  ) : (
                                    <div className="w-full h-24 rounded border border-dashed border-[#ccc] flex items-center justify-center">
                                      <span className="text-xs text-[#aaa]"><Image size={16} className="inline mr-1" />Placeholder</span>
                                    </div>
                                  )}
                                  {!readOnly && (
                                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => uploadHero(row.subdivisionId, 'hero_invitacion_url')}
                                      disabled={!!uploadingHero} data-testid={`btn-upload-hero-inv-${row.subdivisionId || 'general'}`}>
                                      {uploadingHero === `${row.subdivisionId || 'general'}:hero_invitacion_url` ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />} Subir imagen
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Hero Confirmación */}
                            <div>
                              <p className="text-xs font-medium text-[#888] mb-2">Hero Confirmaci&oacute;n Entrega</p>
                              {heroConf ? (
                                <div className="space-y-2">
                                  <div className="relative group cursor-pointer" onClick={() => setPreviewHeroUrl(heroConf)}>
                                    <img src={heroConf} alt="Hero confirmación" className="w-full h-24 object-cover rounded border" data-testid={`hero-conf-preview-${row.subdivisionId || 'general'}`} />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded flex items-center justify-center">
                                      <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  </div>
                                  {!readOnly && (
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => uploadHero(row.subdivisionId, 'hero_confirmacion_url')}
                                        disabled={!!uploadingHero} data-testid={`btn-change-hero-conf-${row.subdivisionId || 'general'}`}>
                                        {uploadingHero === `${row.subdivisionId || 'general'}:hero_confirmacion_url` ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />} Cambiar
                                      </Button>
                                      <Button size="sm" variant="ghost" className="text-xs h-7 text-red-500" onClick={() => deleteHero(row.config.id, 'hero_confirmacion_url')}
                                        data-testid={`btn-del-hero-conf-${row.subdivisionId || 'general'}`}>
                                        <Trash2 size={12} className="mr-1" /> Eliminar
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {inheritedConf ? (
                                    <div>
                                      <div className="relative group cursor-pointer" onClick={() => setPreviewHeroUrl(inheritedConf)}>
                                        <img src={inheritedConf} alt="Heredada" className="w-full h-24 object-cover rounded border opacity-40" />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded flex items-center justify-center">
                                          <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                      </div>
                                      <p className="text-xs text-[#aaa] italic mt-1">(hereda: General)</p>
                                    </div>
                                  ) : (
                                    <div className="w-full h-24 rounded border border-dashed border-[#ccc] flex items-center justify-center">
                                      <span className="text-xs text-[#aaa]"><Image size={16} className="inline mr-1" />Placeholder</span>
                                    </div>
                                  )}
                                  {!readOnly && (
                                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => uploadHero(row.subdivisionId, 'hero_confirmacion_url')}
                                      disabled={!!uploadingHero} data-testid={`btn-upload-hero-conf-${row.subdivisionId || 'general'}`}>
                                      {uploadingHero === `${row.subdivisionId || 'general'}:hero_confirmacion_url` ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />} Subir imagen
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  });
                })()}

                <div className="flex items-center gap-2 text-xs text-[#888] px-1">
                  <Info size={14} />
                  <span>Si no se configura imagen, se usa un placeholder gen&eacute;rico de Imagina para los correos.</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog: Preview Hero Image */}
      <Dialog open={!!previewHeroUrl} onOpenChange={() => setPreviewHeroUrl(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="dialog-hero-preview">
          <DialogHeader className="sr-only">
            <DialogTitle>Vista previa</DialogTitle>
          </DialogHeader>
          {previewHeroUrl && (
            <img
              src={previewHeroUrl}
              alt="Hero preview"
              className="w-full max-h-[80vh] object-contain rounded"
              data-testid="hero-preview-img"
            />
          )}
        </DialogContent>
      </Dialog>


      {/* Dialog: Nuevo Documento */}
      <Dialog open={showAddDoc} onOpenChange={setShowAddDoc}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nuevo Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Nombre</Label>
              <Input
                value={newDocNombre}
                onChange={(e) => setNewDocNombre(e.target.value)}
                placeholder="Ej: Acta de entrega firmada"
                className="mt-1"
                data-testid="input-doc-nombre"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newDocObligatorio}
                onChange={(e) => setNewDocObligatorio(e.target.checked)}
                id="doc-obligatorio"
              />
              <Label htmlFor="doc-obligatorio" className="text-sm">Obligatorio</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddDoc(false)}>Cancelar</Button>
              <Button onClick={handleCrearDocumento} className="bg-[#4E7A79] hover:bg-[#3d6362] text-white" data-testid="btn-guardar-doc">
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nueva Actividad Checklist */}
      <Dialog open={showAddChecklist} onOpenChange={setShowAddChecklist}>
        <DialogContent className="max-w-sm" data-testid="dialog-nueva-actividad-checklist">
          <DialogHeader>
            <DialogTitle>Nueva Actividad</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Nombre <span className="text-red-500">*</span></Label>
              <Input
                value={newCheckNombre}
                onChange={(e) => setNewCheckNombre(e.target.value)}
                placeholder="Ej: Verificar pago de cuota inicial"
                className="mt-1"
                data-testid="input-checklist-nombre"
              />
            </div>
            <div>
              <Label className="text-sm">Responsable <span className="text-xs text-[#888]">(opcional)</span></Label>
              <Popover open={openComboResp} onOpenChange={setOpenComboResp}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openComboResp}
                    className="w-full mt-1 justify-between font-normal"
                    data-testid="sel-checklist-responsable"
                  >
                    {newCheckResponsable && newCheckResponsable !== 'none'
                      ? allUsuarios.find(u => u.id === newCheckResponsable)?.nombre || 'Cualquier usuario'
                      : 'Cualquier usuario'}
                    <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar usuario..." />
                    <CommandList>
                      <CommandEmpty>Sin resultados</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="cualquier-usuario"
                          onSelect={() => { setNewCheckResponsable('none'); setOpenComboResp(false); }}
                        >
                          <Check size={14} className={`mr-2 ${!newCheckResponsable || newCheckResponsable === 'none' ? 'opacity-100' : 'opacity-0'}`} />
                          Cualquier usuario
                        </CommandItem>
                        {allUsuarios.map(u => (
                          <CommandItem
                            key={u.id}
                            value={u.nombre}
                            onSelect={() => { setNewCheckResponsable(u.id); setOpenComboResp(false); }}
                          >
                            <Check size={14} className={`mr-2 ${newCheckResponsable === u.id ? 'opacity-100' : 'opacity-0'}`} />
                            {u.nombre}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowAddChecklist(false)}>Cancelar</Button>
              <Button
                onClick={handleCrearChecklistItem}
                className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
                data-testid="btn-guardar-checklist-item"
              >
                <Save size={14} className="mr-1" />
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MasivaCell = ({ etiqueta, rowKey, rowSpan, checked, saving, disabled, onChange }) => (
  <td className="px-3 py-2 align-top border-l border-[#E8E8E7] pointer-events-auto" rowSpan={rowSpan}>
    <label
      className={`flex items-start gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      title={`${etiqueta}: entrega masiva (no se validan cruces de horario del arquitecto)`}
    >
      <input
        type="checkbox"
        className="w-4 h-4 mt-0.5 shrink-0 accent-[#4E7A79] disabled:cursor-not-allowed"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        data-testid={`checkbox-masiva-${rowKey}`}
      />
      <span className="text-[11px] leading-tight text-[#666]" data-testid={`label-masiva-${rowKey}`}>{etiqueta}</span>
      {saving && <Loader2 size={13} className="animate-spin text-[#5B7FA6] shrink-0 mt-0.5" />}
    </label>
  </td>
);

export default EntregaConfiguracion;
